import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Filter, FilterDocumentType} from '@/types/filter';
import {PersistConfig, persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

interface FiltersState {
  group: Filter;
  event: Filter;
  onDemandEvent: Filter;
  user: Filter;
}

export const initialState: FiltersState = {
  group: {
    sports: [],
    myTeams: false,
    location: undefined,
    distance: 15000,
    accessType: ['PUBLIC'],
    textSearch: null,
    locationLabel: null,
    companies: []
  },
  event: {
    sports: [],
    location: undefined,
    distance: 15,
    textSearch: null,
    locationLabel: null,
    isPaid: null,
    online: null,
    group: null,
    start: null,
    startTime: null,
    endTime: null,
    pastEvents: false,
    weekdays: [1, 2, 3, 4, 5, 6, 7],
    ageGroups: ['ALL'],
    companies: []
  },
  onDemandEvent: {
    sports: [],
    textSearch: null,
    isPaid: null,
    ageGroups: ['ALL']
  },
  user: {
    sports: [],
    myTeams: false,
    location: undefined,
    distance: 15000
  }
};

export const filterPersistConfig: PersistConfig<FiltersState> = {
  key: 'filter',
  storage
};

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    updateFilter: (
      state,
      action: PayloadAction<{
        type: FilterDocumentType;
        data: Partial<Filter>;
      }>
    ) => {
      const {type, data} = action.payload;
      const currentState = state[type];

      if (data.myTeams !== undefined) {
        currentState.myTeams = data.myTeams;
        currentState.accessType = data.myTeams
          ? ['PUBLIC', 'PRIVATE']
          : ['PUBLIC'];
      }

      if (!data.location) {
        data.location = undefined;
        data.locationLabel = null;
      }

      if (data.textSearch === '') {
        data.textSearch = null;
      }

      state[type] = {...state[type], ...data};
    },
    resetFilter: (
      state,
      action: PayloadAction<{
        type: FilterDocumentType;
        preserveLocation?: boolean;
      }>
    ) => {
      const {type, preserveLocation} = action.payload;
      if (preserveLocation) {
        const {location, locationLabel, distance} = state[type];
        state[type] = {
          ...initialState[type],
          location,
          locationLabel,
          distance
        };
      } else {
        state[type] = initialState[type];
      }
      state[type] = initialState[type];
    },
    setLocations: (
      state,
      action: PayloadAction<{
        longitude: number;
        latitude: number;
        label?: string;
      }>
    ) => {
      const {longitude, latitude, label} = action.payload;
      if (longitude && latitude && label) {
        state.event.location = {
          coordinates: [longitude, latitude]
        };
        state.event.locationLabel = label;
        state.group.location = {
          coordinates: [longitude, latitude]
        };
        state.group.locationLabel = label;
      }
    },
    setSports: (state, action: PayloadAction<string>) => {
      const sportsKey = action.payload;
      state.event.sports = [sportsKey];
      state.group.sports = [sportsKey];
    }
  }
});

export const {updateFilter, resetFilter, setLocations, setSports} =
  filterSlice.actions;
export default persistReducer(filterPersistConfig, filterSlice.reducer);
